import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import frLocale from "date-fns/locale/fr";
import { useSnackbar } from "notistack";

import {
  EditLocation as EditLocationIcon,
  PersonAdd as PersonAddIcon
} from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  FormControlLabel,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { addDoc, collection, getDocs } from "firebase/firestore";

import CustomerForm from "../components/CustomerForm";
import LocationForm from "../components/LocationForm";
import { useAuth } from "../contexts/AuthContext";
import { db } from "../firebase";

const NewAppointment = () => {
  const { currentUser } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [isHomeAppointment, setIsHomeAppointment] = useState(false);
  const [locations, setLocations] = useState([]);
  const [openClientDialog, setOpenClientDialog] = useState(false);
  const [openLocationDialog, setOpenLocationDialog] = useState(false);

  useEffect(() => {
    const fetchClients = async () => {
      if (!currentUser?.uid) return;

      const clientsRef = collection(db, `users/${currentUser.uid}/customers`);
      const snapshot = await getDocs(clientsRef);
      const clientsList = snapshot.docs.map(currentDoc => ({
        id: currentDoc.id,
        ...currentDoc.data()
      }));
      setClients(clientsList);
    };

    fetchClients();
  }, [currentUser]);

  const handleLocationSave = newLocation => {
    setLocations([newLocation]);
  };

  const handleCustomerSave = customer => {
    setClients(prev => [...prev, customer]);
    setSelectedClient(customer);
    setOpenClientDialog(false);
  };

  const handleHomeToggle = event => {
    const checked = event.target.checked;
    setIsHomeAppointment(checked);
    if (checked) {
      setLocations([]);
    }
  };

  const handleSave = async () => {
    if (!selectedClient || !selectedDate) {
      enqueueSnackbar(t("appointment.notifications.requiredFields"), {
        variant: "error"
      });
      return;
    }

    if (!isHomeAppointment && locations.length === 0) {
      enqueueSnackbar(t("appointment.notifications.locationRequired"), {
        variant: "error"
      });
      return;
    }

    const selectedDateISO = selectedDate.toISOString();
    const mappedLocations = isHomeAppointment
      ? [{
        place_id: null,
        locationWhere: t("agenda.homeLabel"),
        eventName: t("appointment.homeVisit"),
        isDefault: true,
        isHome: true,
        eventDate: selectedDateISO
      }]
      : locations.map(location => ({
        place_id: location.place?.place_id || null,
        locationWhere: location.place
          ? location.place.description || location.place.formatted_address || ""
          : "",
        eventName: location.eventName || "",
        isDefault: true,
        isHome: false,
        eventDate: location.eventDate?.toISOString?.() || selectedDateISO
      }));

    const now = new Date().toISOString();

    const appointmentData = {
      orderType: "appointment",
      isAppointment: true,
      client_id: selectedClient.id,
      selectedDate: selectedDateISO,
      locations: mappedLocations,
      catalog_id: null,
      servicePrice: 0,
      fees: [],
      discounts: [],
      totalPrice: 0,
      workflow: { id: null, tasks: [] },
      paymentPlan: "null",
      paymentDetails: [],
      orderIsConfirmed: false,
      orderIsCanceled: false,
      createdAt: now,
      updatedAt: now
    };

    try {
      await addDoc(collection(db, `users/${currentUser.uid}/orders`), appointmentData);
      enqueueSnackbar(t("appointment.notifications.saveSuccess"), {
        variant: "success"
      });
      navigate("/agenda");
    } catch (error) {
      enqueueSnackbar(t("appointment.notifications.saveError") + error.message, {
        variant: "error"
      });
    }
  };

  return (
    <Box sx={{ maxWidth: "100%", mb: 4 }}>
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: { xs: 2.5, md: 3 },
          borderRadius: 3,
          border: "1px solid #e2e8f0",
          background: theme =>
            theme.palette.mode === "dark"
              ? "linear-gradient(135deg, rgba(148, 163, 184, 0.14), rgba(71, 85, 105, 0.16))"
              : "#ffffff"
        }}
      >
        <Typography
          variant="h4"
          sx={{
            mb: 0.5,
            color: theme => theme.palette.mode === "dark" ? "primary.light" : "#0f172a",
            fontWeight: 800
          }}
        >
          {t("appointment.title")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("appointment.subtitle")}
        </Typography>
      </Paper>

      <Paper sx={{ p: 3, borderRadius: 3, border: "1px solid #e2e8f0" }}>
        <Stack spacing={2.25}>
          <Autocomplete
            options={clients}
            value={selectedClient}
            onChange={(event, value) => setSelectedClient(value)}
            getOptionLabel={option => option ? `${option.firstname || ""} ${option.lastname || ""}`.trim() : ""}
            renderInput={params => (
              <TextField
                {...params}
                label={t("appointment.client")}
                placeholder={t("appointment.clientPlaceholder")}
              />
            )}
          />

          <Box>
            <Button
              variant="outlined"
              startIcon={<PersonAddIcon />}
              onClick={() => setOpenClientDialog(true)}
            >
              {t("reservation.client.addNew")}
            </Button>
          </Box>

          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={frLocale}>
            <DatePicker
              label={t("appointment.date")}
              value={selectedDate}
              onChange={newValue => setSelectedDate(newValue)}
              renderInput={params => <TextField {...params} fullWidth />}
            />
          </LocalizationProvider>

          <FormControlLabel
            control={
              <Checkbox
                checked={isHomeAppointment}
                onChange={handleHomeToggle}
              />
            }
            label={t("appointment.atHome")}
          />

          <Button
            variant="outlined"
            startIcon={<EditLocationIcon />}
            onClick={() => setOpenLocationDialog(true)}
            disabled={isHomeAppointment}
          >
            {t("reservation.location.add")}
          </Button>

          {!isHomeAppointment && locations.length > 0 && (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t("reservation.location.date")}</TableCell>
                    <TableCell>{t("reservation.location.place")}</TableCell>
                    <TableCell>{t("reservation.location.note")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {locations.map((location, index) => (
                    <TableRow key={`${location.place?.place_id || "no-place"}-${index}`}>
                      <TableCell>
                        {new Date(location.eventDate).toLocaleString("fr-FR")}
                      </TableCell>
                      <TableCell>
                        {location.place?.description || location.place?.formatted_address || ""}
                      </TableCell>
                      <TableCell>{location.eventName || "-"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Box>
            <Button
              variant="contained"
              onClick={handleSave}
              sx={{
                borderRadius: 2.5,
                height: 48,
                px: 3.5,
                textTransform: "none",
                fontWeight: 700,
                backgroundColor: "#d4a411",
                color: "#221d10",
                "&:hover": {
                  backgroundColor: "#c2960f"
                }
              }}
            >
              {t("appointment.save")}
            </Button>
          </Box>
        </Stack>
      </Paper>

      <Dialog open={openClientDialog} onClose={() => setOpenClientDialog(false)} maxWidth="md" fullWidth>
        <CustomerForm
          open={openClientDialog}
          handleClose={() => setOpenClientDialog(false)}
          onSave={handleCustomerSave}
        />
      </Dialog>

      <Dialog open={openLocationDialog} onClose={() => setOpenLocationDialog(false)} maxWidth="sm" fullWidth>
        <LocationForm
          selectedDate={selectedDate || new Date()}
          onClose={() => setOpenLocationDialog(false)}
          onSave={handleLocationSave}
        />
      </Dialog>
    </Box>
  );
};

export default NewAppointment;
